import {
  CORE_CHANNELS, prepareReport, rankAdvertisers, passesFilters, reportHeader,
  scopedKeywordsAndPostcodes, keywordsBySearches, highestAveragePosition, mean, round,
} from "./reportCore";

// Every number on the Market Overview page. Pure: no React, no database,
// no browser, no clock - the same input always gives the same result, so the
// app and (later) the MCP server return identical numbers.
// The result shape is documented in marketOverview.md. The counting itself
// (searches, who each row belongs to, ranking) is shared with
// advertiserOverview() in reportCore.js.

export const MARKET_OVERVIEW_VERSION = 1;

// For the other report pages (PPC ...), and the Advertisers filter's list.
export { filterAppearancesByType, advertiserOptions } from "./reportCore";

// Google's usual UK page layout, top to bottom. Used because map pack rows
// have no page position, and ads usually come from a separate google_ads page.
const UK_SERP_LAYOUT = ["lsa", "ppc", "shopping", "local", "ai", "seo"];
const NOT_COLLECTED = ["ai"];

export function marketOverview(data, filters = {}) {
  const report = prepareReport(data, filters);
  const { f, completed, of, identities, serp, shoppingUnidentified, towns } = report;

  // ---- Advertiser rows, after the Business type and Advertisers filters, ranked ----
  const rows = rankAdvertisers(report, a => passesFilters(a, f));
  const visibleKeys = new Set(rows.map(r => r.key));

  // ---- Grid: keyword x postcode, plus "any keyword" per postcode ----
  const { keywords, postcodes } = scopedKeywordsAndPostcodes(report);
  const grid = [];
  for (const postcode of postcodes) {
    for (const keyword of [...keywords, null]) {
      const cell = completed.filter(s => s.postcode === postcode && (keyword === null || s.keyword === keyword));
      const seen = { ppc: new Set(), local: new Set(), seo: new Set(), shopping: new Set() };
      for (const search of cell) {
        for (const row of search.rows) {
          const id = identities.get(row);
          if (!id || id.unidentified || !visibleKeys.has(id.key) || !seen[id.channel]) continue;
          seen[id.channel].add(id.key);
        }
      }
      grid.push({
        keyword, postcode, town: towns[postcode] || null, searches: cell.length,
        ppcAdvertisers: seen.ppc.size, localListings: seen.local.size,
        channels: { ppc: seen.ppc.size, local: seen.local.size, seo: seen.seo.size, shopping: seen.shopping.size },
      });
    }
  }

  const cells = grid.filter(g => g.keyword !== null && g.searches > 0);
  const postcodeRows = grid.filter(g => g.keyword === null && g.searches > 0);
  const gapSummary = {
    cells: cells.length,
    cellsWithNoAds: cells.filter(g => g.ppcAdvertisers === 0).length,
    postcodes: postcodeRows.length,
    postcodesWithNoAds: postcodeRows.filter(g => g.ppcAdvertisers === 0).length,
    keywordsBySearches: keywordsBySearches(keywords, completed),
  };

  // ---- Totals ----
  const ppcPairs = rows.reduce((sum, r) => sum + (r.channels.ppc?.searches || 0), 0);
  const top3Pairs = rows.map(r => r.channels.ppc?.searches || 0).sort((a, b) => b - a).slice(0, 3).reduce((x, y) => x + y, 0);
  const totals = {
    advertisers: rows.length,
    ppc: rows.filter(r => r.channels.ppc).length,
    local: rows.filter(r => r.channels.local).length,
    seo: rows.filter(r => r.channels.seo).length,
    shopping: rows.filter(r => r.channels.shopping).length,
    lsa: rows.filter(r => r.channels.lsa).length,
    multiChannel: rows.filter(r => CORE_CHANNELS.filter(c => r.channels[c]).length >= 2).length,
    avgAdsPerSearch: of > 0 ? round(ppcPairs / of, 1) : 0,
    top3PpcShare: ppcPairs > 0 ? round(top3Pairs / ppcPairs, 2) : null,
    shoppingUnidentified,
  };

  // ---- Business types: share of appearances (ads, map pack, organic) ----
  const byType = {};
  for (const r of rows) {
    const t = byType[r.businessType.type] || (byType[r.businessType.type] = { type: r.businessType.type, advertisers: 0, appearances: 0 });
    t.advertisers++;
    t.appearances += r.visibilityAppearances;
  }
  const typeList = Object.values(byType);
  const shares = sharesSummingToOne(typeList.map(t => t.appearances));
  typeList.forEach((t, i) => { t.share = shares[i]; });
  typeList.sort((a, b) => b.share - a.share || b.advertisers - a.advertisers || a.type.localeCompare(b.type));

  // ---- SERP map ----
  const serpMap = UK_SERP_LAYOUT.map(channel => {
    if (NOT_COLLECTED.includes(channel)) return { channel, collected: false };
    const block = serp[channel];
    return {
      channel,
      shownIn: block ? block.shownIn : 0,
      of,
      avgStartPosition: block && block.starts.length > 0 ? round(mean(block.starts), 1) : null,
    };
  });

  for (const r of rows) delete r.visibilityAppearances;

  return {
    version: MARKET_OVERVIEW_VERSION,
    ...reportHeader(report, data.samples),
    serpMap,
    serpMapSource: "fixed_uk_fallback",
    totals,
    businessTypes: typeList,
    advertisers: rows,
    grid,
    gapSummary,
    searchVolume: searchVolume(postcodes, towns),
    facts: buildFacts(rows, of, gapSummary, totals),
  };
}

// ---------- Facts ----------

// The only claims AdAudit makes. A fact is left out when there's nothing to say.
function buildFacts(rows, of, gapSummary, totals) {
  const facts = [];
  const top = rows[0];
  if (top && top.visibility.searches > 0) {
    facts.push({ claim: "seen_most_often", advertiser: top.displayName, value: top.visibility.searches, of });
  }

  // Ads if anyone ran them, otherwise the map pack, otherwise organic.
  // Only advertisers seen in at least 2 searches in that channel, so one
  // lucky search can't win. Left out if nobody qualifies.
  for (const channel of CORE_CHANNELS) {
    const best = highestAveragePosition(rows, channel);
    if (best) {
      facts.push({ claim: "highest_average_position", channel, advertiser: best.displayName, value: best.channels[channel].avgPosition });
      break;
    }
  }

  // Share of ad appearances held by the top 3 ad advertisers - only said
  // when more than 3 advertisers ran ads (otherwise it's always 100%).
  if (totals.ppc > 3 && totals.top3PpcShare != null) {
    facts.push({ claim: "top_3_ad_share", value: totals.top3PpcShare });
  }

  const priced = rows.filter(r => r.channels.shopping?.withPrice > 0)
    .sort((a, b) => b.channels.shopping.withPrice - a.channels.shopping.withPrice || a.rank - b.rank)[0];
  if (priced) {
    facts.push({ claim: "shown_with_a_price", advertiser: priced.displayName, value: priced.channels.shopping.withPrice, of });
  }

  if (gapSummary.cells > 0) {
    facts.push({ claim: "no_ads_seen", cells: gapSummary.cellsWithNoAds, of: gapSummary.cells });
  }

  if (top && top.firstSeenAt) {
    facts.push({ claim: "first_seen", advertiser: top.displayName, at: top.firstSeenAt });
  }
  return facts;
}

// ---------- Search volume (slot only, not available yet) ----------

function searchVolume(postcodes, towns) {
  const counts = {};
  for (const p of postcodes) if (towns[p]) counts[towns[p]] = (counts[towns[p]] || 0) + 1;
  const topTown = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  const areaName = topTown
    ? topTown[0]
    : postcodes.length > 1 ? `${postcodes[0]} +${postcodes.length - 1}` : postcodes[0] || null;
  return {
    status: "not_available",
    level: "area",
    areaName,
    low: null,
    high: null,
    source: "google_keyword_planner_estimate",
    label: areaName ? `Search volume · ${areaName}` : "Search volume",
  };
}

// Shares to 3 decimal places that add up to exactly 1.
function sharesSummingToOne(values) {
  const total = values.reduce((x, y) => x + y, 0);
  if (total === 0) return values.map(() => 0);
  const exact = values.map(v => (v / total) * 1000);
  const whole = exact.map(Math.floor);
  let left = 1000 - whole.reduce((x, y) => x + y, 0);
  const order = exact.map((v, i) => [v - Math.floor(v), i]).sort((a, b) => b[0] - a[0]);
  for (const [, i] of order) {
    if (left <= 0) break;
    whole[i]++;
    left--;
  }
  return whole.map(v => v / 1000);
}
